import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { AuditLogEntry } from 'api/resources/audit/types';
import { getProjectAuditLogs } from 'api/resources/changeLogStream';
import { curryGetFunction } from 'api/utils';
import useProjectRoute from 'hooks/useProjectRoute';

export const generateProjectChangeLogStreamKey = (
  accountId: string,
  projectId: string,
  limit?: number,
  cursor?: string,
) => [accountId, projectId, 'audit-change-log', limit, cursor].filter(Boolean);

export const NUM_LOGS_PER_PAGE = 50;

const useProjectChangeLogStream = (limit?: number, cursor?: string) => {
  const { accountId, projectId } = useProjectRoute();
  const queryClient = useQueryClient();

  const pullNewData = useCallback(() => {
    void queryClient.invalidateQueries(
      generateProjectChangeLogStreamKey(accountId, projectId),
      { exact: false },
    );
  }, [queryClient, accountId, projectId]);

  return {
    ...useQuery({
      queryKey: generateProjectChangeLogStreamKey(
        accountId,
        projectId,
        limit,
        cursor,
      ),
      queryFn: curryGetFunction(getProjectAuditLogs)(
        accountId,
        projectId,
        limit,
        cursor,
      ),
      keepPreviousData: true,
    }),
    pullNewData,
  };
};

export const useFetchLatestProjectChangeLogsStream = () => {
  const { accountId, projectId } = useProjectRoute();

  return useCallback(
    (callback: (response: AuditLogEntry[]) => void) =>
      getProjectAuditLogs(accountId, projectId).then(callback),
    [accountId, projectId],
  );
};

export default useProjectChangeLogStream;
